<script setup lang="ts">
/**
 * Página pública de produto monitorado: comparação de preços entre lojas
 * + gráfico de histórico (dados do monitor de preços).
 */
interface OfferStore {
  id: string
  name: string
  slug: string
  logoUrl: string | null
}

interface ProductOffer {
  id: string
  url: string
  lastPrice: number | null
  lastOriginalPrice: number | null
  inStock: boolean
  lastCheckedAt: string | null
  store: OfferStore
}

interface ProductDeal {
  id: string
  title: string
  slug: string
  price: number | null
  originalPrice: number | null
  discount: number | null
  shortCode: string | null
  publishedAt: string
  store: { name: string, slug: string, logoUrl: string | null }
}

interface Product {
  id: string
  slug: string
  title: string
  brand: string | null
  model: string | null
  ean: string | null
  imageUrl: string | null
  category: { id: string, name: string, slug: string } | null
  offers: ProductOffer[]
  deals: ProductDeal[]
}

interface HistorySeries {
  offerId: string
  store: OfferStore
  currentPrice: number | null
  inStock: boolean
  lowest: number | null
  highest: number | null
  points: Array<{ date: string, min: number, max: number, avg: number }>
}

const route = useRoute()
const config = useRuntimeConfig()
const slug = route.params.slug as string

const historyDays = ref(90)

const [{ data: product, error }, { data: history, status: historyStatus }] = await Promise.all([
  useFetch<Product>(`/products/${slug}`, {
    baseURL: config.public.apiBaseUrl,
    key: `product-${slug}`,
  }),
  useFetch<{ series: HistorySeries[] }>(
    () => `/products/${slug}/price-history?days=${historyDays.value}`,
    {
      baseURL: config.public.apiBaseUrl,
      key: `product-history-${slug}`,
      watch: [historyDays],
    },
  ),
])

if (error.value || !product.value) {
  throw createError({ statusCode: 404, statusMessage: 'Produto não encontrado' })
}

const series = computed(() => history.value?.series ?? [])

const inStockOffers = computed(() =>
  product.value!.offers.filter(o => o.inStock && o.lastPrice !== null),
)

const bestOffer = computed(() => {
  const offers = [...inStockOffers.value].sort((a, b) => (a.lastPrice ?? Infinity) - (b.lastPrice ?? Infinity))
  return offers[0] ?? null
})

const lowestEver = computed(() => {
  const values = series.value.map(s => s.lowest).filter((v): v is number => v !== null)
  return values.length > 0 ? Math.min(...values) : null
})

const isLowestNow = computed(() =>
  bestOffer.value?.lastPrice !== null
  && lowestEver.value !== null
  && (bestOffer.value?.lastPrice ?? Infinity) <= lowestEver.value,
)

function formatMoney(value: number | null): string {
  if (value === null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDateTime(value: string | null): string {
  if (!value) return ''
  return new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const dayOptions = [
  { label: '30 dias', value: 30 },
  { label: '90 dias', value: 90 },
  { label: '6 meses', value: 180 },
  { label: '1 ano', value: 365 },
]

useSeoMeta({
  title: () => `${product.value?.title} — Histórico de Preços | Descontito`,
  description: () => `Compare o preço de ${product.value?.title} em ${product.value?.offers.length ?? 0} lojas e veja o histórico para saber se a promoção é real.`,
  ogImage: () => product.value?.imageUrl ?? undefined,
})
</script>

<template>
  <div v-if="product" class="max-w-5xl mx-auto px-4 py-6 space-y-6">
    <!-- Cabeçalho do produto -->
    <div class="flex flex-col sm:flex-row gap-6">
      <div class="w-full sm:w-48 shrink-0">
        <div class="aspect-square rounded-xl bg-white border border-gray-200 dark:border-gray-800 flex items-center justify-center overflow-hidden">
          <NuxtImg
            v-if="product.imageUrl"
            :src="product.imageUrl"
            :alt="product.title"
            class="object-contain w-full h-full p-2"
          />
          <UIcon v-else name="i-heroicons-cube" class="w-12 h-12 text-gray-300" />
        </div>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap mb-2">
          <NuxtLink to="/produtos" class="text-xs text-gray-400 hover:text-primary-500">
            Monitor de Preços
          </NuxtLink>
          <span class="text-xs text-gray-300">·</span>
          <UBadge v-if="product.category" color="neutral" variant="soft" size="sm">
            {{ product.category.name }}
          </UBadge>
          <UBadge v-if="isLowestNow" color="success" variant="solid" size="sm">
            🔥 Menor preço do período
          </UBadge>
        </div>

        <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {{ product.title }}
        </h1>
        <p v-if="product.brand" class="text-sm text-gray-400 mt-1">
          {{ product.brand }}<template v-if="product.ean"> · EAN {{ product.ean }}</template>
        </p>

        <div v-if="bestOffer" class="mt-4">
          <p class="text-xs text-gray-400 uppercase tracking-wide">Melhor preço agora</p>
          <div class="flex items-baseline gap-3 flex-wrap">
            <span class="text-3xl font-black text-primary-500">{{ formatMoney(bestOffer.lastPrice) }}</span>
            <span class="text-sm text-gray-500">na {{ bestOffer.store.name }}</span>
          </div>
          <UButton
            :to="bestOffer.url"
            target="_blank"
            rel="nofollow sponsored"
            color="primary"
            size="lg"
            icon="i-heroicons-shopping-cart"
            class="mt-3"
          >
            Ver na {{ bestOffer.store.name }}
          </UButton>
        </div>
        <p v-else class="mt-4 text-sm text-gray-400">
          Sem ofertas disponíveis no momento.
        </p>
      </div>
    </div>

    <!-- Comparação entre lojas -->
    <UCard>
      <template #header>
        <h2 class="text-sm font-medium text-gray-900 dark:text-white">Comparar preços nas lojas</h2>
      </template>

      <div class="divide-y divide-gray-100 dark:divide-gray-800">
        <div
          v-for="offer in product.offers"
          :key="offer.id"
          class="flex items-center gap-3 py-3"
        >
          <NuxtImg
            v-if="offer.store.logoUrl"
            :src="offer.store.logoUrl"
            :alt="offer.store.name"
            class="w-6 h-6 rounded object-contain"
          />
          <div v-else class="w-6 h-6 rounded bg-gray-100 dark:bg-gray-800" />

          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium">{{ offer.store.name }}</p>
            <p class="text-xs text-gray-400">Atualizado {{ formatDateTime(offer.lastCheckedAt) }}</p>
          </div>

          <div class="text-right">
            <p
              class="font-bold"
              :class="offer.inStock ? (offer.id === bestOffer?.id ? 'text-primary-500 text-lg' : 'text-gray-900 dark:text-white') : 'text-gray-400 line-through'"
            >
              {{ formatMoney(offer.lastPrice) }}
            </p>
            <UBadge v-if="!offer.inStock" color="neutral" variant="soft" size="sm">Indisponível</UBadge>
          </div>

          <UButton
            :to="offer.url"
            target="_blank"
            rel="nofollow sponsored"
            color="neutral"
            variant="soft"
            size="sm"
            icon="i-heroicons-arrow-top-right-on-square"
          >
            Ir à loja
          </UButton>
        </div>
      </div>
    </UCard>

    <!-- Histórico de preços -->
    <UCard>
      <template #header>
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 class="text-sm font-medium text-gray-900 dark:text-white">Histórico de preços</h2>
            <p class="text-xs text-gray-400 mt-0.5">
              Saiba se a promoção é real: compare com o que o produto realmente custou
              <template v-if="lowestEver !== null"> · menor preço registrado: <span class="font-semibold">{{ formatMoney(lowestEver) }}</span></template>
            </p>
          </div>
          <div class="flex gap-1">
            <UButton
              v-for="opt in dayOptions"
              :key="opt.value"
              size="xs"
              :color="historyDays === opt.value ? 'primary' : 'neutral'"
              :variant="historyDays === opt.value ? 'solid' : 'soft'"
              @click="historyDays = opt.value"
            >
              {{ opt.label }}
            </UButton>
          </div>
        </div>
      </template>

      <div :class="{ 'opacity-50': historyStatus === 'pending' }">
        <ProductPriceHistoryChart :series="series" />
      </div>
    </UCard>

    <!-- Ofertas ativas do produto -->
    <UCard v-if="product.deals.length > 0">
      <template #header>
        <h2 class="text-sm font-medium text-gray-900 dark:text-white">Ofertas ativas deste produto</h2>
      </template>

      <div class="space-y-2">
        <NuxtLink
          v-for="deal in product.deals"
          :key="deal.id"
          :to="`/oferta/${deal.slug}`"
          class="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
        >
          <UIcon name="i-heroicons-tag" class="w-5 h-5 text-primary-500 shrink-0" />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate">{{ deal.title }}</p>
            <p class="text-xs text-gray-400">{{ deal.store.name }}</p>
          </div>
          <div class="text-right shrink-0">
            <p class="font-bold text-primary-500">{{ formatMoney(deal.price) }}</p>
            <p v-if="deal.discount" class="text-xs text-green-600">-{{ deal.discount }}% real</p>
          </div>
        </NuxtLink>
      </div>
    </UCard>
  </div>
</template>
